import React, { useState, useEffect } from 'react';
import { dispatchCall, dispatchBulkCalls, getUserCredits } from '../services/api';

function CallDispatcher() {
  const [phoneNumbers, setPhoneNumbers] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [agentName, setAgentName] = useState('Rachel');
  const [callerCompany, setCallerCompany] = useState('The Tech Marketer');
  const [customerTitle, setCustomerTitle] = useState('Founder');
  const [customerCompany, setCustomerCompany] = useState('Market Wavegen');
  const [assets, setAssets] = useState('Signal First Demand Generation Content');
  const [accountId, setAccountId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [credits, setCredits] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    loadCredits();
  }, []);

  const loadCredits = async () => {
    try {
      const response = await getUserCredits();
      if (response.success) {
        setCredits(response.callingCredit ?? response.credits ?? 0);
        setIsAdmin(response.role === 'admin');
      }
    } catch (err) {
      console.error('Error loading credits:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setProgress(null);
    setLoading(true);

    if (!phoneNumbers || !phoneNumbers.trim()) {
      setError('Phone number(s) are required');
      setLoading(false);
      return;
    }

    const numbersArray = phoneNumbers
      .split(/[,\n\r]+/)
      .map(num => num.trim())
      .filter(num => num.length > 0);

    if (numbersArray.length === 0) {
      setError('Please enter at least one phone number');
      setLoading(false);
      return;
    }

    const formattedNumbers = [];
    for (const num of numbersArray) {
      const formatted = num.startsWith('+') ? num : `+${num}`;
      if (!/^\+[1-9]\d{1,14}$/.test(formatted)) {
        setError(`Invalid phone number format: ${num}. Use E.164 format (e.g., +919077000077)`);
        setLoading(false);
        return;
      }
      formattedNumbers.push(formatted);
    }

    try {
      // Build call context with RetellAI dynamic variables
      // These map to retell_llm_dynamic_variables in the API call:
      // - agent_name: Name of the AI agent calling
      // - caller_company: Company the agent is calling from
      // - customer_name: Name of the person being called
      // - customer_title: Job title of the person being called
      // - customer_company: Company of the person being called
      // - assets: Content/service being discussed
      const callContext = {
        // RetellAI dynamic variables for conversation flow:
        // "Hello — this is {{agent_name}} calling from The {{caller_company}}."
        agent_name: agentName?.trim() || 'Rachel',
        // "Hello — this is {{agent_name}} calling from The {{caller_company}}."
        caller_company: callerCompany?.trim() || 'The Tech Marketer',
        // "I believe I'm speaking with {{customer_name}}, the {{customer_title}} of {{customer_company}}?"
        customer_name: customerName?.trim() || '',
        // "I believe I'm speaking with {{customer_name}}, the {{customer_title}} of {{customer_company}}?"
        customer_title: customerTitle?.trim() || 'Founder',
        // "I believe I'm speaking with {{customer_name}}, the {{customer_title}} of {{customer_company}}?"
        customer_company: customerCompany?.trim() || 'Market Wavegen',
        // "Just to confirm — did you interact with the {{assets}}?"
        assets: assets?.trim() || 'Signal First Demand Generation Content',
        // Optional tracking fields
        account_id: accountId?.trim() || ''
      };

      // Log context for debugging
      console.log('📞 RetellAI Call Context:', callContext);

      if (formattedNumbers.length === 1) {
        const response = await dispatchCall(formattedNumbers[0], callContext);
        
        if (response.success) {
          setResult({
            success: true,
            message: response.message || 'Call dispatched successfully via RetellAI!',
            data: response.data,
            callInfo: response.call_info,
            callId: response.data?.call_id
          });
          if (response.creditsRemaining !== null && response.creditsRemaining !== undefined) {
            setCredits(response.creditsRemaining);
          }
          setPhoneNumbers('');
          setCustomerName('');
          setCustomerTitle('Founder');
          setAccountId('');
          loadCredits();
        } else {
          setError(response.message || 'Failed to dispatch call');
          loadCredits();
        }
      } else {
        setProgress({ 
          current: 0, 
          total: formattedNumbers.length, 
          status: `Processing ${formattedNumbers.length} calls with 3-second delay between each...` 
        });
        
        const response = await dispatchBulkCalls(formattedNumbers, callContext);
        
        if (response.success) {
          setResult({
            success: true,
            message: `Successfully dispatched ${response.successful} of ${response.total} calls via RetellAI`,
            results: response.results,
            total: response.total,
            successful: response.successful,
            failed: response.failed
          });
          if (response.creditsRemaining !== null && response.creditsRemaining !== undefined) {
            setCredits(response.creditsRemaining);
          }
          setPhoneNumbers('');
          setCustomerName('');
          setCustomerTitle('Founder');
          setAccountId('');
          loadCredits();
        } else {
          setError(response.message || 'Failed to dispatch calls');
          loadCredits();
        }
      }
    } catch (err) {
      console.error('Error dispatching call:', err);
      setError(err.response?.data?.message || err.message || 'Failed to dispatch call');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e27] py-8 px-4 relative z-10">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-semibold text-white mb-2">Call Dispatcher</h1>
              <p className="text-sm text-gray-400">Dispatch calls via RetellAI</p>
            </div>
            {credits !== null && (
              <div className="text-right">
                <div className="text-xs text-gray-400 mb-1">Call Credits</div>
                <div className={`text-2xl font-semibold ${credits > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {credits}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg backdrop-blur-sm">
            <div className="flex justify-between items-center">
              <p className="text-red-400 text-sm">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-300 text-xl leading-none"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Main Form */}
        <form onSubmit={handleSubmit} className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 space-y-6 backdrop-blur-sm">
          {/* Phone Number(s) */}
          <div>
            <label htmlFor="phoneNumbers" className="block text-sm font-medium text-gray-300 mb-2">
              Phone Number(s) <span className="text-red-400">*</span>
            </label>
            <textarea
              id="phoneNumbers"
              value={phoneNumbers}
              onChange={(e) => setPhoneNumbers(e.target.value)}
              placeholder="+919077000077&#10;+917874866543"
              rows={4}
              className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono resize-none transition-all"
              required
            />
            <p className="mt-2 text-xs text-gray-400">
              Enter one number per line, or comma-separated. Format: E.164 (e.g., +919190***0077)
            </p>
            <p className="mt-1 text-xs text-blue-400">
              💡 Multiple numbers will be called sequentially with 3-second delay between calls
            </p>
          </div>

          {/* Form Fields Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Customer Name */}
            <div>
              <label htmlFor="customerName" className="block text-sm font-medium text-gray-300 mb-2">
                Customer Name <span className="text-gray-500">(&#123;&#123;customer_name&#125;&#125;)</span>
              </label>
              <input
                type="text"
                id="customerName"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-all"
              />
              <p className="mt-1 text-xs text-gray-400">
                Used in: &quot;I believe I&apos;m speaking with &#123;&#123;customer_name&#125;&#125;...&quot;
              </p>
            </div>

            {/* Customer Title */}
            <div>
              <label htmlFor="customerTitle" className="block text-sm font-medium text-gray-300 mb-2">
                Customer Title <span className="text-gray-500">(&#123;&#123;customer_title&#125;&#125;)</span>
              </label>
              <input
                type="text"
                id="customerTitle"
                value={customerTitle}
                onChange={(e) => setCustomerTitle(e.target.value)}
                placeholder="Founder"
                className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-all"
              />
              <p className="mt-1 text-xs text-gray-400">
                Used in: &quot;...the &#123;&#123;customer_title&#125;&#125; of &#123;&#123;customer_company&#125;&#125;?&quot;
              </p>
            </div>

            {/* Agent Name */}
            <div>
              <label htmlFor="agentName" className="block text-sm font-medium text-gray-300 mb-2">
                Agent Name <span className="text-gray-500">(&#123;&#123;agent_name&#125;&#125;)</span>
              </label>
              <input
                type="text"
                id="agentName"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                placeholder="Rachel"
                className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-all"
              />
              <p className="mt-1 text-xs text-gray-400">
                Used in: &quot;Hello — this is &#123;&#123;agent_name&#125;&#125; calling from...&quot;
              </p>
            </div>

            {/* Caller Company */}
            <div>
              <label htmlFor="callerCompany" className="block text-sm font-medium text-gray-300 mb-2">
                Caller Company <span className="text-gray-500">(&#123;&#123;caller_company&#125;&#125;)</span>
              </label>
              <input
                type="text"
                id="callerCompany"
                value={callerCompany}
                onChange={(e) => setCallerCompany(e.target.value)}
                placeholder="The Tech Marketer"
                className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-all"
              />
              <p className="mt-1 text-xs text-gray-400">
                Used in: &quot;...calling from The &#123;&#123;caller_company&#125;&#125;.&quot;
              </p>
            </div>

            {/* Customer Company */}
            <div>
              <label htmlFor="customerCompany" className="block text-sm font-medium text-gray-300 mb-2">
                Customer Company <span className="text-gray-500">(&#123;&#123;customer_company&#125;&#125;)</span>
              </label>
              <input
                type="text"
                id="customerCompany"
                value={customerCompany}
                onChange={(e) => setCustomerCompany(e.target.value)}
                placeholder="Market Wavegen"
                className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-all"
              />
              <p className="mt-1 text-xs text-gray-400">
                Used in: &quot;...the &#123;&#123;customer_title&#125;&#125; of &#123;&#123;customer_company&#125;&#125;?&quot;
              </p>
            </div>

            {/* Assets */}
            <div>
              <label htmlFor="assets" className="block text-sm font-medium text-gray-300 mb-2">
                Assets <span className="text-gray-500">(&#123;&#123;assets&#125;&#125;)</span>
              </label>
              <input
                type="text"
                id="assets"
                value={assets}
                onChange={(e) => setAssets(e.target.value)}
                placeholder="Signal First Demand Generation Content"
                className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-all"
              />
              <p className="mt-1 text-xs text-gray-400">
                Used in: &quot;Just to confirm — did you interact with the &#123;&#123;assets&#125;&#125;?&quot;
              </p>
            </div>

            {/* Account ID */}
            <div className="md:col-span-2">
              <label htmlFor="accountId" className="block text-sm font-medium text-gray-300 mb-2">
                Account ID <span className="text-gray-500">(Optional - for tracking)</span>
              </label>
              <input
                type="text"
                id="accountId"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                placeholder="ACC-12345"
                className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-all"
              />
            </div>
          </div>

          {/* RetellAI Info Box */}
          <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm text-blue-400 font-medium">Powered by RetellAI</p>
                <p className="text-xs text-gray-400 mt-1">
                  Calls are dispatched via RetellAI API with dynamic variables. 
                  Transcripts, recordings, and AI analysis will be available after each call completes.
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || (credits !== null && credits < 1)}
            className="w-full px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-lg transition-all shadow-lg shadow-blue-600/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {progress ? `Dispatching... (${progress.current}/${progress.total})` : 'Dispatching Call...'}
              </span>
            ) : (
              '📞 Dispatch Call via RetellAI'
            )}
          </button>
          {credits !== null && credits < 1 && (
            <p className="text-sm text-red-400 text-center">
              ⚠️ Insufficient credits. You need at least 1 credit to make a call.
            </p>
          )}
        </form>

        {/* Progress Message */}
        {progress && loading && (
          <div className="mt-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg backdrop-blur-sm">
            <div className="flex items-center mb-3">
              <svg className="animate-spin h-5 w-5 text-blue-400 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-blue-400 text-sm font-medium">{progress.status || `Processing ${progress.current} of ${progress.total}...`}</p>
            </div>
            <div className="w-full bg-black/50 border border-gray-700 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {result && result.success && (
          <div className="mt-6 p-4 bg-green-900/20 border border-green-500/30 rounded-lg backdrop-blur-sm">
            <div className="flex items-start mb-3">
              <svg className="w-5 h-5 text-green-400 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-green-400 text-sm font-medium mb-2">{result.message}</p>
                
                {/* Single call result with call ID */}
                {result.callId && (
                  <div className="mt-2 p-2 bg-black/50 rounded border border-gray-700">
                    <p className="text-xs text-gray-400">
                      Call ID: <span className="text-white font-mono">{result.callId}</span>
                    </p>
                  </div>
                )}
                
                {/* Bulk call summary */}
                {result.total && (
                  <div className="mt-3 p-3 bg-black/50 rounded-lg border border-gray-700">
                    <p className="text-xs font-medium text-gray-300 mb-2">Summary:</p>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-gray-400">Total:</span>
                        <span className="ml-2 font-semibold text-white">{result.total}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Success:</span>
                        <span className="ml-2 font-semibold text-green-400">{result.successful}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Failed:</span>
                        <span className="ml-2 font-semibold text-red-400">{result.failed}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bulk call results list */}
                {result.results && result.results.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-300 mb-2">Call Results:</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {result.results.map((res, idx) => (
                        <div key={idx} className="p-2 bg-black/50 rounded border border-gray-700 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="text-white font-mono">{res.to_number || res.number}</span>
                            <span className={`px-2 py-1 rounded ${
                              res.success ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                            }`}>
                              {res.success ? 'Dispatched' : 'Failed'}
                            </span>
                          </div>
                          {res.call_log_id && (
                            <p className="text-gray-500 mt-1">Log ID: {res.call_log_id}</p>
                          )}
                          {res.message && !res.success && (
                            <p className="text-red-400 mt-1">{res.message}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CallDispatcher;
